import React, { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';

const API = 'https://api.glowytics.com';

export default function TsoftSalesLab() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const [baseUrl, setBaseUrl] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [limit, setLimit] = useState(20);
  const [sort, setSort] = useState('-createDate');
  const [include, setInclude] = useState('customer,products');
  const [revenueOnly, setRevenueOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  const headers = useMemo(
    () => ({
      'Content-Type': 'application/json',
      ...(brandId ? { 'X-Brand-ID': brandId } : {}),
    }),
    [brandId]
  );

  const fetchOrders = async () => {
    setLoading(true);
    setPayload(null);
    try {
      const endpoint = revenueOnly ? 'tsoft/revenue' : 'tsoft/orders';
      const res = await fetch(`${API}/api/labs/${endpoint}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          baseUrl: baseUrl.trim() || undefined,
          email: email.trim() || undefined,
          password: password || undefined,
          query: { limit, sort, ...(revenueOnly ? {} : { include }) },
          ...(revenueOnly ? { maxPages: 5 } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || data?.details || `Request failed (${res.status})`);
      setPayload(data);
      toast.success('Data fetched');
    } catch (e) {
      console.error('[TsoftSalesLab] error', e);
      toast.error(e.message || 'Request failed');
      setPayload({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setLoading(true);
    setPayload(null);
    try {
      const res = await fetch(`${API}/api/labs/tsoft/test`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          baseUrl: baseUrl.trim() || undefined,
          email: email.trim() || undefined,
          password: password || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || data?.details || `Request failed (${res.status})`);
      setPayload(data);
      toast.success('Connection test successful!');
    } catch (e) {
      console.error('[TsoftSalesLab] test error', e);
      toast.error(e.message || 'Test failed');
      setPayload({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Labs · T-Soft Sales</h1>
        <p className="text-sm text-gray-500 mt-1">
          Fetch orders via backend proxy (REST1 API token is handled server-side).
        </p>
      </div>

      <div className="bg-white border rounded-xl p-4 space-y-4">
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Store domain</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://hupalupa.store"
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">WS Username</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="glowytics"
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">WS Password</label>
            <input
              type="password"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              autoComplete="new-password"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Limit</label>
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              min={1}
              max={200}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Sort</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              placeholder="-createDate"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Include</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              value={include}
              onChange={(e) => setInclude(e.target.value)}
              placeholder="customer,products"
              disabled={revenueOnly}
            />
          </div>
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={revenueOnly} onChange={(e) => setRevenueOnly(e.target.checked)} />
          Revenue only (no customer/products)
        </label>

        <div className="flex flex-wrap gap-3">
          <button
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${
              loading ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'
            }`}
            onClick={testConnection}
            disabled={loading}
          >
            {loading ? 'Testing…' : 'Test Connection'}
          </button>
          <button
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${
              loading ? 'bg-gray-400' : 'bg-slate-900 hover:bg-black'
            }`}
            onClick={fetchOrders}
            disabled={loading}
          >
            {loading ? 'Fetching…' : revenueOnly ? 'Fetch revenue' : 'Fetch orders'}
          </button>
          <button
            className="px-4 py-2 rounded-lg text-sm font-semibold border"
            onClick={() => setPayload(null)}
            disabled={loading}
          >
            Clear
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Response</h2>
        </div>
        {payload ? (
          <pre className="bg-gray-50 rounded-lg p-4 text-xs overflow-x-auto">
            {typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2)}
          </pre>
        ) : (
          <div className="text-sm text-gray-500">No response yet.</div>
        )}
      </div>
    </div>
  );
}

